import React, { useState } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Modal, ModalHeader, ModalBody, Row, Col } from 'reactstrap';

const STATUS_CONFIG = {
    good:     { color: '#28a745', label: 'Bom',     bg: '#d4edda' },
    warning:  { color: '#ffc107', label: 'Atenção', bg: '#fff3cd' },
    critical: { color: '#dc3545', label: 'Crítico', bg: '#f8d7da' },
};

const LocationTrendCard = ({ location }) => {
    const [isOpen, setIsOpen] = useState(false);
    const config = STATUS_CONFIG[location.status] || STATUS_CONFIG.good;

    return (
        <>
            <div
                onClick={() => setIsOpen(true)}
                style={{
                    cursor: 'pointer',
                    border: `2px solid ${config.color}`,
                    borderRadius: '10px',
                    padding: '12px',
                    backgroundColor: config.bg,
                    marginBottom: '10px',
                    transition: 'transform 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: '#2c3e50' }}>
                            {location.room}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>{location.floor}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{
                            background: config.color,
                            color: 'white',
                            borderRadius: '12px',
                            padding: '2px 10px',
                            fontSize: '11px',
                            fontWeight: 600,
                        }}>
                            {config.label}
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: config.color, marginTop: '4px' }}>
                            {location.positive_rate}%
                        </div>
                        <div style={{ fontSize: '10px', color: '#666' }}>positivos</div>
                    </div>
                </div>

                {/* Mini sparkline */}
                <div style={{ marginTop: '8px' }}>
                    <ResponsiveContainer width="100%" height={40}>
                        <LineChart data={location.timeline}>
                            <Line
                                type="monotone"
                                dataKey="positive"
                                stroke={config.color}
                                dot={false}
                                strokeWidth={2}
                                isAnimationActive={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div style={{ fontSize: '10px', color: '#888', marginTop: '4px', textAlign: 'right' }}>
                    Clique para ver detalhes →
                </div>
            </div>

            {/* Modal de detalhes */}
            <Modal isOpen={isOpen} toggle={() => setIsOpen(false)} size="lg" zIndex={999999}>
                <ModalHeader toggle={() => setIsOpen(false)} className="edit-popup">
                    📍 {location.room} — {location.floor}
                </ModalHeader>
                <ModalBody>
                    <Row style={{ marginBottom: '16px' }}>
                        <Col md={4}>
                            <div style={{
                                textAlign: 'center', padding: '16px',
                                background: config.bg, borderRadius: '10px',
                                border: `2px solid ${config.color}`
                            }}>
                                <div style={{ fontSize: '32px', fontWeight: 'bold', color: config.color }}>
                                    {location.positive_rate}%
                                </div>
                                <div style={{ fontSize: '12px', color: '#666' }}>Taxa de Positivos</div>
                                <div style={{
                                    background: config.color, color: 'white',
                                    borderRadius: '12px', padding: '2px 12px',
                                    fontSize: '12px', fontWeight: 600, marginTop: '8px', display: 'inline-block'
                                }}>
                                    {config.label}
                                </div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div style={{ textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: '10px' }}>
                                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#dc3545' }}>
                                    {location.total_positive}
                                </div>
                                <div style={{ fontSize: '12px', color: '#666' }}>Total Positivos</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div style={{ textAlign: 'center', padding: '16px', background: '#f8f9fa', borderRadius: '10px' }}>
                                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#28a745' }}>
                                    {location.total_negative}
                                </div>
                                <div style={{ fontSize: '12px', color: '#666' }}>Total Negativos</div>
                            </div>
                        </Col>
                    </Row>

                    <div style={{ fontWeight: 600, marginBottom: '8px', color: '#2c3e50' }}>
                        Tendência ao longo do tempo
                    </div>
                    <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={location.timeline} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" fontSize={10} />
                            <YAxis fontSize={10} allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="positive" name="Positivos" stroke="#dc3545" strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="negative" name="Negativos" stroke="#28a745" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </ModalBody>
            </Modal>
        </>
    );
};

const LocationTrend = ({ trendData, loading }) => {
    if (loading) {
        return <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Carregando tendências...</div>;
    }

    if (!trendData || trendData.length === 0) {
        return <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Nenhum dado de tendência disponível</div>;
    }

    const critical = trendData.filter(l => l.status === 'critical');
    const warning  = trendData.filter(l => l.status === 'warning');
    const good     = trendData.filter(l => l.status === 'good');

    return (
        <div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ background: '#f8d7da', borderRadius: '8px', padding: '8px 16px', fontSize: '12px' }}>
                    🔴 <strong>{critical.length}</strong> Crítico(s)
                </div>
                <div style={{ background: '#fff3cd', borderRadius: '8px', padding: '8px 16px', fontSize: '12px' }}>
                    🟡 <strong>{warning.length}</strong> Atenção
                </div>
                <div style={{ background: '#d4edda', borderRadius: '8px', padding: '8px 16px', fontSize: '12px' }}>
                    🟢 <strong>{good.length}</strong> Bom
                </div>
            </div>

            <div style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: '4px' }}>
                {[...critical, ...warning, ...good].map((loc, i) => (
                    <LocationTrendCard key={i} location={loc} />
                ))}
            </div>
        </div>
    );
};

export default LocationTrend;